import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { User } from '../../../types';
import '../styles/EditEventModal.css';
import { toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import { createDateInZone, DEFAULT_TZ, formatTimeInZone, getTimeZoneLabel } from '../../../shared/utils/timezone';
import { isSupportingAssignmentCovered } from '../utils/effectiveEventStaffing';
import type { EventParticipant } from '../services/eventParticipants';
import { EVENT_STAFF_ROLES, type EventStaffRole, type EventSupportingStaff } from '../utils/eventSupportingStaff';

interface EditEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedEvent: any) => void;
  event: any;
  teachers: User[];
  eventTimeZone?: string;
  canEditSupportingStaff?: boolean;
  regularLeadTeacherId?: string;
  /** Admin only: assign a chosen teacher to the event's open sub request. */
  onFillSubRequest?: (substituteTeacherId: string) => Promise<void>;
}

const EditEventModal: React.FC<EditEventModalProps> = ({ isOpen, onClose, onSave, event, teachers, eventTimeZone = DEFAULT_TZ, canEditSupportingStaff = true, regularLeadTeacherId, onFillSubRequest }) => {
  const [teacherId, setTeacherId] = useState('');
  const [supportingTeacherIds, setSupportingTeacherIds] = useState<string[]>([]);
  const [supportingStaff, setSupportingStaff] = useState<EventSupportingStaff>({});
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [title, setTitle] = useState('');
  const [eventParticipants, setEventParticipants] = useState<EventParticipant[]>([]);
  const [participantUserId, setParticipantUserId] = useState('');
  const [participantRole, setParticipantRole] = useState<EventStaffRole>('co_teacher');
  const [participantPay, setParticipantPay] = useState('');
  const [participantPayEdited, setParticipantPayEdited] = useState(false);
  const [fillSubTeacherId, setFillSubTeacherId] = useState('');
  const [fillSubBusy, setFillSubBusy] = useState(false);
  const [fillSubError, setFillSubError] = useState('');
  const timeZone = String(eventTimeZone || '').trim() || DEFAULT_TZ;
  const timeZoneLabel = getTimeZoneLabel(timeZone);

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setSupportingStaff(event.extendedProps.supportingStaff || {});
      setSaveError('');
      // Edit the stored assignment, never the V2 effective-coverage overlay.
      setTeacherId(event.extendedProps.assignedMainTeacherId ?? event.extendedProps.mainTeacherId ?? '');
      setSupportingTeacherIds(event.extendedProps.assignedSupportingTeacherIds ?? event.extendedProps.supportingTeacherIds ?? []);
      setEventParticipants(event.extendedProps.eventParticipants || []);
      setParticipantUserId('');
      setParticipantRole('co_teacher');
      setParticipantPay('');
      setParticipantPayEdited(false);
      setFillSubTeacherId('');
      setFillSubError('');

      // Convert event times to the squad's timezone and format for input.
      if (event.start) {
        const zonedStart = toZonedTime(event.start, timeZone);
        setStartTime(format(zonedStart, 'HH:mm'));
      }
      if (event.end) {
        const zonedEnd = toZonedTime(event.end, timeZone);
        setEndTime(format(zonedEnd, 'HH:mm'));
      }
    }
  }, [event, timeZone]);

  if (!isOpen || !event) {
    return null;
  }

  const assignedLeadTeacherId = String(event.extendedProps?.assignedMainTeacherId ?? event.extendedProps?.mainTeacherId ?? '').trim();
  const teacherTodayId = String(event.extendedProps?.mainTeacherId || assignedLeadTeacherId).trim();
  const regularTeacherId = String(regularLeadTeacherId || assignedLeadTeacherId).trim();
  const teacherName = (id: string) => teachers.find(teacher => teacher.id === id)?.displayName || (id ? 'Unknown teacher' : 'None');
  const hasActiveCoverage = Array.isArray(event.extendedProps?.activeCoverage) && event.extendedProps.activeCoverage.length > 0;
  const leadTeacherIsSubbing = Boolean(teacherTodayId && regularTeacherId && teacherTodayId !== regularTeacherId);

  const participantPayInvalid = participantPay !== '' && (!Number.isFinite(Number(participantPay)) || Number(participantPay) < 0);

  const handleSave = async () => {
    if (Object.values(supportingStaff).some(item => item.hourlyRate !== null && (!Number.isFinite(item.hourlyRate) || item.hourlyRate < 0))) {
      setSaveError('Enter a valid hourly rate of $0 or more, or leave it blank.');
      return;
    }
    const originalDate = event.start;
    const eventDate = formatTimeInZone(originalDate, timeZone, 'yyyy-MM-dd');
    const newStartDate = createDateInZone(eventDate, startTime, timeZone);
    const newEndDate = createDateInZone(eventDate, endTime, timeZone);
  
    setSaving(true);
    setSaveError('');
    try { await onSave({
      id: event.id,
      title: title,
      mainTeacherId: teacherId,
      supportingTeacherIds,
      supportingStaff,
      eventParticipants,
      start: newStartDate,
      end: newEndDate,
      squadId: event.extendedProps.squadId
    }); } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not save the event. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div className="edit-event-modal-overlay" onClick={onClose}>
      <div className="edit-event-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-group">
          <label htmlFor="event-title">Event Title</label>
          <input
            id="event-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="event-title-input"
          />
        </div>
        <div className="form-group">
          <div><strong>Regular lead teacher:</strong> {teacherName(regularTeacherId)}</div>
          <label htmlFor="teacher-select">Teacher today</label>
          {hasActiveCoverage ? (
            <>
              <select id="teacher-select" value={teacherTodayId} disabled>
                <option value={teacherTodayId}>{teacherName(teacherTodayId)}</option>
              </select>
              {leadTeacherIsSubbing && <small>{teacherName(teacherTodayId)} is substituting for {teacherName(regularTeacherId)} today.</small>}
            </>
          ) : (
            <select id="teacher-select" value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
              <option value="">None</option>
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.displayName}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="form-group-row">
          <div className="form-group">
            <label htmlFor="start-time">Start Time ({timeZoneLabel})</label>
            <input
              id="start-time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="end-time">End Time ({timeZoneLabel})</label>
            <input
              id="end-time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>
        {(() => {
          if (!onFillSubRequest) return null;
          const coverage = Array.isArray(event.extendedProps?.activeCoverage) ? event.extendedProps.activeCoverage[0] : null;
          if (!coverage || coverage.status !== 'open' || event.extendedProps?.subStatus !== 'requested') return null;
          const requesterId = String(event.extendedProps?.subRequestedByTeacherId || '');
          const coveredName = teachers.find(t => t.id === String(coverage.requestedTeacherId || ''))?.displayName || 'the requesting teacher';
          const roleLabel = coverage.requestedRole === 'co_teacher' ? 'Co-teacher' : 'Lead teacher';
          return (
            <div className="form-group">
              <label htmlFor="fill-sub-select">Fill sub request</label>
              <div className="form-group-row">
                <select id="fill-sub-select" value={fillSubTeacherId} disabled={fillSubBusy} onChange={(e) => { setFillSubTeacherId(e.target.value); setFillSubError(''); }}>
                  <option value="">Choose substitute</option>
                  {[...teachers]
                    .filter(t => t.id !== requesterId)
                    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
                    .map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
                </select>
                <button type="button" disabled={!fillSubTeacherId || fillSubBusy} onClick={async () => {
                  setFillSubBusy(true);
                  setFillSubError('');
                  try {
                    await onFillSubRequest(fillSubTeacherId);
                    onClose();
                  } catch (error) {
                    setFillSubError(error instanceof Error ? error.message : 'Could not assign the substitute. Please try again.');
                  } finally {
                    setFillSubBusy(false);
                  }
                }}>{fillSubBusy ? 'Assigning…' : 'Assign sub'}</button>
              </div>
              <small>Covering for {coveredName} · {roleLabel}. Assigning fills the open request immediately, just like the teacher claiming it.</small>
              {fillSubError && <p role="alert">{fillSubError}</p>}
            </div>
          );
        })()}
        <div className="form-group">
          <label htmlFor="event-participant-search">Supporting people for this class</label>
          <div className="event-person-composer">
          <Select
            inputId="event-participant-search"
            isSearchable
            isClearable
            placeholder="Search team members…"
            noOptionsMessage={() => 'No matching team members'}
            options={[...teachers]
              .filter(teacher => !supportingTeacherIds.includes(teacher.id) && teacher.id !== teacherId && teacher.id !== teacherTodayId && !eventParticipants.some(item => item.userId === teacher.id))
              .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
              .map(teacher => ({ value: teacher.id, label: teacher.displayName || teacher.id }))}
            value={participantUserId ? { value: participantUserId, label: teacherName(participantUserId) } : null}
            onChange={option => setParticipantUserId(option?.value || '')}
            className="event-participant-search"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
          <div className="event-person-fields">
            <label>Role
              <select disabled={!canEditSupportingStaff} aria-label="Class role" value={participantRole} onChange={e => {
                const role = e.target.value as EventStaffRole;
                setParticipantRole(role);
                if (!participantPayEdited) setParticipantPay(role === 'observer' ? '0' : '');
              }}>
                {EVENT_STAFF_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            {canEditSupportingStaff && <label htmlFor="new-person-pay">Hourly pay
              <div className="event-person-pay">
                <span aria-hidden="true">$</span>
                <input id="new-person-pay" type="number" min="0" step="0.01" inputMode="decimal" placeholder="Existing rate" value={participantPay}
                  aria-invalid={participantPayInvalid} aria-describedby="new-person-pay-help"
                  onChange={e => { setParticipantPay(e.target.value); setParticipantPayEdited(true); }} />
                <span aria-hidden="true">/hr</span>
              </div>
            </label>}
          </div>
          {canEditSupportingStaff && <small id="new-person-pay-help" className={participantPayInvalid ? 'event-person-error' : 'event-person-hint'}>
            {participantPayInvalid ? 'Enter $0 or more, or leave blank for the existing rate.' : 'For this class only. Leave blank to use their existing rate.'}
          </small>}
          <button className="event-person-add" type="button" disabled={!participantUserId || participantPayInvalid} onClick={() => {
            if (!participantUserId || participantPayInvalid) return;
            setSupportingTeacherIds(prev => [...prev, participantUserId]);
            setSupportingStaff(prev => ({ ...prev, [participantUserId]: { role: participantRole, hourlyRate: participantPay === '' ? null : Number(participantPay) } }));
            setParticipantUserId('');
            setParticipantPay(participantRole === 'observer' ? '0' : '');
            setParticipantPayEdited(false);
          }}><span aria-hidden="true">＋</span> Add person</button>
          <small className="event-person-hint">Then select Save Changes to confirm.</small>
          </div>
          <details className="event-person-access">
            <summary>Class access and substitute coverage</summary>
            <p>Everyone added receives squad access; roles do not restrict permissions. Removing someone from this class does not revoke squad access.</p>
            {hasActiveCoverage && <p>You can add people while a substitute is assigned. Supporting people unrelated to the substitution can be removed. Teachers involved in coverage must remain assigned until coverage is resolved.</p>}
          </details>
          {supportingTeacherIds.map(id => {
            const name = teacherName(id);
            const assignment = supportingStaff[id] || { role: 'co_teacher' as const, hourlyRate: null };
            const existingDuringCoverage = hasActiveCoverage && isSupportingAssignmentCovered(id, event.extendedProps.activeCoverage);
            return <div className="event-supporting-staff" key={id}>
              <div className="event-person-header">
                <strong>{name}</strong>
              <button className="event-person-remove" type="button" aria-label={`Remove ${name}`} title={existingDuringCoverage ? 'Resolve substitute coverage before removing this assignment' : `Remove ${name} from this class`} disabled={existingDuringCoverage} onClick={() => {
                setSupportingTeacherIds(prev => prev.filter(item => item !== id));
                setSupportingStaff(prev => { const next = { ...prev }; delete next[id]; return next; });
              }}><svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M9 6V4h6v2M5 6l1 14h12l1-14M10 10v6M14 10v6" /></svg>Remove</button>
              </div>
              <label>Class role<select disabled={!canEditSupportingStaff} aria-label={`Staff role for ${name}`} value={assignment.role} onChange={e => setSupportingStaff(prev => ({ ...prev, [id]: { ...assignment, role: e.target.value as EventStaffRole } }))}>
                {EVENT_STAFF_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select></label>
              {canEditSupportingStaff && <label>Hourly pay ($/hr)<input aria-label={`Hourly pay for ${name}`} type="number" min="0" step="0.01" placeholder="Existing rate" value={assignment.hourlyRate ?? ''} onChange={e => setSupportingStaff(prev => ({ ...prev, [id]: { ...assignment, hourlyRate: e.target.value === '' ? null : Number(e.target.value) } }))} /></label>}

            </div>;
          })}
          {eventParticipants.map(item => (
            <div key={item.userId} className="form-group-row">
              <span>{teachers.find(teacher => teacher.id === item.userId)?.displayName || item.userId} · {item.role.replace(/_/g, ' ')}</span>
              <button className="event-person-remove" type="button" onClick={() => setEventParticipants(prev => prev.filter(existing => existing.userId !== item.userId))}>Remove</button>
            </div>
          ))}
        </div>
        <div className="modal-actions">
          {saveError && <p role="alert">{saveError}</p>}
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-save" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEventModal;
